import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Divider,
  Autocomplete,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Breadcrumbs,
  Link,
  Alert,
  CircularProgress,
  Chip,
  Tooltip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';

import { salesOrderService } from '../../../services/salesOrderService';
import { estimateService } from '../../../services/estimateService';
import { CustomerService } from '../../../services/customerService';
import { productService } from '../../../services/productService';
import { warehouseService } from '../../../services/warehouseService';
import { customerAddressService, CustomerAddress } from '../../../services/customerAddressService';
import { CustomerQuickAddDialog } from '../../../components/customers/CustomerQuickAddDialog';
import { 
  SalesOrderCreate, 
  SalesOrderUpdate, 
  SalesOrderItem, 
  OtherCharge 
} from '../../../types/salesOrder';
import { formatCurrency } from '../../../utils/formatters';

interface SalesOrderFormData extends Omit<SalesOrderCreate, 'items'> {
  items: SalesOrderItem[];
}

const SalesOrderForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const isEditing = Boolean(id);

  // Check if we're converting from an estimate
  const estimateId = new URLSearchParams(location.search).get('estimate_id');
  const isConvertingFromEstimate = Boolean(estimateId);

  // Form state
  const [formData, setFormData] = useState<SalesOrderFormData>({
    customer_id: '',
    order_date: format(new Date(), 'yyyy-MM-dd'),
    expected_delivery_date: format(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'), // 7 days from now
    billing_address_id: '',
    shipping_address_id: '',
    shipping_charges: 0,
    packing_charges: 0,
    other_charges: [],
    notes: '',
    items: []
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [customerAddresses, setCustomerAddresses] = useState<CustomerAddress[]>([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [salesOrder, setSalesOrder] = useState<any>(null);
  const [salesOrderLoading, setSalesOrderLoading] = useState(false);
  const [customersData, setCustomersData] = useState<any>(null);
  const [productsData, setProductsData] = useState<any>(null);
  const [sourceEstimate, setSourceEstimate] = useState<any>(null);
  const [stockInfo, setStockInfo] = useState<Record<string, any>>({});  // Store stock info by variant_id
  
  // Warehouse state - persisted in localStorage
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<string>(() => {
    return localStorage.getItem('admin_selectedWarehouseId') || '';
  });

  // Quick Customer Creation State
  const [openCustomerDialog, setOpenCustomerDialog] = useState(false);
  const [customerGroups, setCustomerGroups] = useState<any[]>([]);

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch customers
        console.log('[SalesOrderForm] Fetching customers...');
        const customers = await CustomerService.getCustomers({ page: 1, per_page: 100 });
        console.log('[SalesOrderForm] Customers fetched:', customers);
        console.log('[SalesOrderForm] Customer count:', customers?.items?.length || 0);
        setCustomersData(customers);

        // Fetch customer groups for quick creation
        const groups = await CustomerService.getCustomerGroups({ page: 1, per_page: 50, is_active: true });
        setCustomerGroups(groups.items || []);

        // Fetch warehouses
        const whData = await warehouseService.getWarehouses();
        setWarehouses(whData);
        // Auto-select first warehouse if none stored
        if (!localStorage.getItem('admin_selectedWarehouseId') && whData.length > 0) {
          setSelectedWarehouseId(whData[0].id);
          localStorage.setItem('admin_selectedWarehouseId', whData[0].id);
        }

        // Fetch products
        const products = await productService.listProducts({ page: 1, per_page: 1000 });
        setProductsData(products);

        // If converting from estimate, fetch estimate data
        if (isConvertingFromEstimate && estimateId) {
          const estimateData = await estimateService.getEstimate(estimateId);
          setSourceEstimate(estimateData);
          
          // Pre-populate form with estimate data
          setFormData({
            customer_id: estimateData.customer_id,
            order_date: format(new Date(), 'yyyy-MM-dd'),
            expected_delivery_date: format(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
            billing_address_id: estimateData.billing_address_id,
            shipping_address_id: estimateData.shipping_address_id,
            shipping_charges: 0,
            packing_charges: 0,
            other_charges: [],
            notes: estimateData.notes || '',
            items: estimateData.items.map((item: any) => ({
              variant_id: item.variant_id,
              quantity: item.quantity,
              unit_price: item.unit_price,
              discount_percent: item.discount_percent,
              description: item.description || '',
              hsn_code: item.hsn_code || ''
            }))
          });
          
          // Fetch stock info for all items
          estimateData.items.forEach((item: any) => {
            if (item.variant_id) {
              fetchStockInfo(item.variant_id);
            }
          });
        }

        // Fetch existing sales order if editing
        if (isEditing && id) {
          setSalesOrderLoading(true);
          const salesOrderData = await salesOrderService.getSalesOrder(id);
          setSalesOrder(salesOrderData);
          setFormData({
            customer_id: salesOrderData.customer_id,
            order_date: salesOrderData.order_date,
            expected_delivery_date: salesOrderData.expected_delivery_date || '',
            billing_address_id: salesOrderData.billing_address_id,
            shipping_address_id: salesOrderData.shipping_address_id,
            shipping_charges: salesOrderData.shipping_charges,
            packing_charges: salesOrderData.packing_charges,
            other_charges: salesOrderData.other_charges,
            notes: salesOrderData.notes || '',
            warehouse_id: salesOrderData.warehouse_id,
            items: salesOrderData.items
          });

          // The order's own fulfillment warehouse is the source of truth when
          // editing — never fall back to whatever warehouse was last picked in
          // this browser's localStorage, or the stock preview shown here won't
          // match what confirm-time validation actually checks.
          if (salesOrderData.warehouse_id) {
            setSelectedWarehouseId(salesOrderData.warehouse_id);
          }

          // Fetch stock info for all items
          salesOrderData.items.forEach((item: any) => {
            if (item.variant_id) {
              fetchStockInfo(item.variant_id, salesOrderData.warehouse_id);
            }
          });
          
          setSalesOrderLoading(false);
        }
      } catch (error: any) {
        console.error('[SalesOrderForm] Failed to fetch data:', error);
        console.error('[SalesOrderForm] Error details:', {
          message: (error as Error)?.message,
          response: (error as any)?.response?.data,
          status: (error as any)?.response?.status
        });
        enqueueSnackbar('Failed to load data', { variant: 'error' });
      }
    };

    fetchData();
  }, [id, isEditing, estimateId, isConvertingFromEstimate, enqueueSnackbar]);

  // Load customer addresses when customer is selected
  useEffect(() => {
    const loadCustomerAddresses = async () => {
      if (formData.customer_id && customersData?.items) {
        const customer = customersData.items.find((c: any) => c.id === formData.customer_id);
        if (customer) {
          setSelectedCustomer(customer);
          setAddressesLoading(true);
          
          try {
            const addressResponse = await customerAddressService.getCustomerAddresses(formData.customer_id);
            setCustomerAddresses(addressResponse.addresses);
            
            // Auto-select default addresses if not already set (only for new orders)
            if (!isEditing && !isConvertingFromEstimate) {
              if (!formData.billing_address_id) {
                const defaultBilling = addressResponse.addresses.find(
                  addr => (addr.address_type === 'billing' || addr.address_type === 'both') && addr.is_default
                );
                if (defaultBilling) {
                  setFormData(prev => ({ ...prev, billing_address_id: defaultBilling.id }));
                }
              }
              
              if (!formData.shipping_address_id) {
                const defaultShipping = addressResponse.addresses.find(
                  addr => (addr.address_type === 'shipping' || addr.address_type === 'both') && addr.is_default
                );
                if (defaultShipping) {
                  setFormData(prev => ({ ...prev, shipping_address_id: defaultShipping.id }));
                }
              }
            }
          } catch (error) {
            console.error('Failed to load customer addresses:', error);
            enqueueSnackbar('Failed to load customer addresses', { variant: 'error' });
          } finally {
            setAddressesLoading(false);
          }
        }
      } else {
        setCustomerAddresses([]);
        setSelectedCustomer(null);
      }
    };

    loadCustomerAddresses();
  }, [formData.customer_id, customersData, isEditing, isConvertingFromEstimate, enqueueSnackbar]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      // Validate form
      const validationErrors: Record<string, string> = {};
      
      if (!formData.customer_id) {
        validationErrors.customer_id = 'Customer is required';
      }
      
      if (!formData.items.length) {
        validationErrors.items = 'At least one item is required';
      } else {
        // Validate each item
        formData.items.forEach((item, index) => {
          if (!item.variant_id) {
            validationErrors[`item_${index}_variant`] = 'Product is required';
          }
          const qty = Number(item.quantity);
          if (isNaN(qty) || qty <= 0) {
            validationErrors[`item_${index}_quantity`] = 'Quantity must be greater than 0';
          }
          const price = Number(item.unit_price);
          if (isNaN(price) || price < 0.01) {
            validationErrors[`item_${index}_price`] = 'Unit price must be at least ₹0.01';
          }
        });
      }
      
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        enqueueSnackbar('Please fix the validation errors', { variant: 'error' });
        return;
      }

      // Ensure all numeric fields in items are valid numbers
      const cleanedItems = formData.items.map(item => {
        const quantity = Number(item.quantity);
        const unitPrice = Number(item.unit_price);
        const discountPercent = Number(item.discount_percent);
        
        return {
          ...item,
          quantity: isNaN(quantity) || quantity <= 0 ? 1 : quantity,
          unit_price: isNaN(unitPrice) || unitPrice < 0 ? 0 : unitPrice,
          discount_percent: isNaN(discountPercent) || discountPercent < 0 ? 0 : (discountPercent > 100 ? 100 : discountPercent)
        };
      });

      const cleanedFormData = {
        ...formData,
        billing_address_id: formData.billing_address_id && formData.billing_address_id.trim() !== '' ? formData.billing_address_id : undefined,
        shipping_address_id: formData.shipping_address_id && formData.shipping_address_id.trim() !== '' ? formData.shipping_address_id : undefined,
        expected_delivery_date: formData.expected_delivery_date && formData.expected_delivery_date.trim() !== '' ? formData.expected_delivery_date : undefined,
        // The "Dispatch Warehouse" selector was previously display-only and
        // never actually saved to the order — meaning confirm-time stock
        // checks ran against whatever warehouse the order happened to have
        // (often none of the ones the item is actually stocked in). Persist it.
        warehouse_id: selectedWarehouseId || undefined,
        items: cleanedItems
      };

      if (isEditing) {
        await salesOrderService.updateSalesOrder(id!, cleanedFormData as SalesOrderUpdate);
        enqueueSnackbar('Sales order updated successfully', { variant: 'success' });
      } else if (isConvertingFromEstimate && estimateId) {
        // Use the conversion endpoint
        const conversionData = {
          estimate_id: estimateId,
          order_date: formData.order_date,
          expected_delivery_date: formData.expected_delivery_date && formData.expected_delivery_date.trim() !== '' ? formData.expected_delivery_date : undefined,
          shipping_charges: formData.shipping_charges,
          packing_charges: formData.packing_charges,
          other_charges: formData.other_charges,
          notes: formData.notes,
          warehouse_id: selectedWarehouseId || undefined
        };
        const result = await salesOrderService.convertEstimateToSalesOrder(conversionData);
        enqueueSnackbar('Sales order created from estimate successfully', { variant: 'success' });
        navigate(`/admin/documents/sales-orders/${result.sales_order_id}`);
      } else {
        console.log('Submitting sales order with cleaned data:', cleanedFormData);
        const result = await salesOrderService.createSalesOrder(cleanedFormData);
        enqueueSnackbar('Sales order created successfully', { variant: 'success' });
        navigate(`/admin/documents/sales-orders/${result.id}`);
      }
    } catch (error: any) {
      console.error('Failed to save sales order:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to save sales order';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle newly quick-created customer (from CustomerQuickAddDialog)
  const handleQuickCustomerCreated = async (created: any) => {
    // Refresh customers list
    const customers = await CustomerService.getCustomers({ page: 1, per_page: 100 });
    setCustomersData(customers);

    // Select the new customer
    setSelectedCustomer(created);
    setFormData(prev => ({ ...prev, customer_id: created.id }));
    setOpenCustomerDialog(false);
  };

  // Handle adding new line item
  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          variant_id: '',
          quantity: 1,
          unit_price: 0,
          discount_percent: 0,
          description: '',
          hsn_code: ''
        }
      ]
    }));
  };

  // Fetch stock info for a variant (filtered by selected warehouse)
  const fetchStockInfo = async (variantId: string, warehouseId?: string) => {
    try {
      const token = localStorage.getItem('token');
      const wh = warehouseId || selectedWarehouseId;
      const url = wh
        ? `/api/admin/inventory/stock?search=${variantId}&warehouse_id=${wh}`
        : `/api/admin/inventory/stock?search=${variantId}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.items && data.items.length > 0) {
          const stock = data.items[0];
          setStockInfo(prev => ({
            ...prev,
            [variantId]: {
              current_stock: stock.current_stock,
              reserved_stock: stock.reserved_stock,
              available_stock: stock.available_stock,
              is_low_stock: stock.is_low_stock
            }
          }));
        } else {
          // No stock record for this warehouse
          setStockInfo(prev => ({
            ...prev,
            [variantId]: { current_stock: 0, reserved_stock: 0, available_stock: 0, is_low_stock: true }
          }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch stock info:', error);
    }
  };

  // Refetch all item stock when warehouse changes
  useEffect(() => {
    if (selectedWarehouseId && formData.items.length > 0) {
      formData.items.forEach(item => {
        if (item.variant_id) fetchStockInfo(item.variant_id, selectedWarehouseId);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWarehouseId]);

  // Handle removing line item
  const removeLineItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Handle line item changes
  const updateLineItem = (index: number, field: keyof SalesOrderItem, value: any) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
    
    // Clear item-specific errors when user makes changes
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`item_${index}_variant`];
      delete newErrors[`item_${index}_quantity`];
      delete newErrors[`item_${index}_price`];
      return newErrors;
    });
  };

  // Handle adding other charges
  const addOtherCharge = () => {
    setFormData(prev => ({
      ...prev,
      other_charges: [
        ...(prev.other_charges || []),
        { name: '', amount: 0, description: '' }
      ]
    }));
  };

  // Handle removing other charges
  const removeOtherCharge = (index: number) => {
    setFormData(prev => ({
      ...prev,
      other_charges: (prev.other_charges || []).filter((_, i) => i !== index)
    }));
  };

  // Handle other charge changes
  const updateOtherCharge = (index: number, field: keyof OtherCharge, value: any) => {
    setFormData(prev => ({
      ...prev,
      other_charges: (prev.other_charges || []).map((charge, i) => 
        i === index ? { ...charge, [field]: value } : charge
      )
    }));
  };

  // Calculate totals
  const calculateTotals = () => {
    const itemsSubtotal = formData.items.reduce((sum, item) => {
      const qty = Number(item.quantity) || 0;
      const price = Number(item.unit_price) || 0;
      const discountPercent = Number(item.discount_percent) || 0;
      const lineTotal = qty * price;
      const discountAmount = lineTotal * (discountPercent / 100);
      return sum + (lineTotal - discountAmount);
    }, 0);

    const otherChargesTotal = (formData.other_charges || []).reduce((sum, charge) => sum + Number(charge.amount || 0), 0);
    const subtotal = itemsSubtotal + Number(formData.shipping_charges || 0) + Number(formData.packing_charges || 0) + otherChargesTotal;
    const taxAmount = subtotal * 0.18; // 18% GST
    const total = subtotal + taxAmount;

    return { itemsSubtotal, otherChargesTotal, subtotal, taxAmount, total };
  };

  const { itemsSubtotal, otherChargesTotal, subtotal, taxAmount, total } = calculateTotals();

  // Check if any items have insufficient stock
  const hasInsufficientStock = formData.items.some(item => 
    item.variant_id && 
    stockInfo[item.variant_id] && 
    item.quantity > stockInfo[item.variant_id].available_stock
  );

  const insufficientStockItems = formData.items.filter(item => 
    item.variant_id && 
    stockInfo[item.variant_id] && 
    item.quantity > stockInfo[item.variant_id].available_stock
  );

  // Clear errors when form data changes
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const newErrors = { ...errors };
      
      // Clear customer error if customer is selected
      if (formData.customer_id && newErrors.customer_id) {
        delete newErrors.customer_id;
      }
      
      // Clear address errors if addresses are selected
      if (formData.billing_address_id && newErrors.billing_address_id) {
        delete newErrors.billing_address_id;
      }
      
      if (formData.shipping_address_id && newErrors.shipping_address_id) {
        delete newErrors.shipping_address_id;
      }
      
      // Clear items error if items exist
      if (formData.items.length > 0 && newErrors.items) {
        delete newErrors.items;
      }
      
      setErrors(newErrors);
    }
  }, [formData.customer_id, formData.billing_address_id, formData.shipping_address_id, formData.items.length, errors]);

  if (salesOrderLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link color="inherit" href="/admin/dashboard">
          Dashboard
        </Link>
        <Link color="inherit" href="/admin/documents/sales-orders">
          Sales Orders
        </Link>
        <Typography color="text.primary">
          {isEditing ? 'Edit Sales Order' : isConvertingFromEstimate ? 'Convert Estimate to Sales Order' : 'New Sales Order'}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/admin/documents/sales-orders')}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4">
              {isEditing ? `Edit Sales Order ${salesOrder?.order_number}` : 
               isConvertingFromEstimate ? 'Convert Estimate to Sales Order' : 
               'Create New Sales Order'}
            </Typography>
            {isConvertingFromEstimate && sourceEstimate && (
              <Box mt={1}>
                <Chip 
                  label={`Converting from Estimate ${sourceEstimate.estimate_number}`}
                  color="info"
                  size="small"
                />
              </Box>
            )}
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Sales Order'}
        </Button>
      </Box>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={1.5}>
          {/* Stock Warning Alert */}
          {hasInsufficientStock && (
            <Grid item xs={12}>
              <Alert severity="warning" sx={{ mb: 0 }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  ⚠ Insufficient Stock Warning
                </Typography>
                <Typography variant="body2">
                  Some items exceed available stock. Order can be created as DRAFT but cannot be confirmed until stock is adjusted or quantities are reduced.
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {insufficientStockItems.map((item, idx) => {
                    const variant = productsData?.items?.flatMap((p: any) => 
                      (p.variants || []).map((v: any) => ({
                        ...v,
                        product_name: p.name
                      }))
                    ).find((v: any) => v.id === item.variant_id);
                    
                    return (
                      <Typography key={idx} variant="caption" display="block">
                        • {variant?.product_name || 'Unknown'} - {variant?.name || 'Unknown'}: 
                        Requested {item.quantity}, Available {stockInfo[item.variant_id]?.available_stock || 0}
                      </Typography>
                    );
                  })}
                </Box>
              </Alert>
            </Grid>
          )}
          
          {/* Customer Information - Single Row */}
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Grid container spacing={1.5} alignItems="flex-start">
                  {/* Warehouse Selector - full width banner */}
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center" gap={1.5}
                      sx={{
                        bgcolor: selectedWarehouseId ? 'primary.50' : 'warning.50',
                        border: '1px solid',
                        borderColor: selectedWarehouseId ? 'primary.200' : 'warning.300',
                        borderRadius: 1,
                        px: 2,
                        py: 1,
                      }}
                    >
                      <Typography variant="body2" fontWeight={600} color={selectedWarehouseId ? 'primary.main' : 'warning.dark'} sx={{ whiteSpace: 'nowrap' }}>
                        🏭 Dispatch Warehouse:
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: 220 }}>
                        <Select
                          value={selectedWarehouseId}
                          onChange={(e) => {
                            const wid = e.target.value;
                            setSelectedWarehouseId(wid);
                            localStorage.setItem('admin_selectedWarehouseId', wid);
                            // Refresh stock for all current items
                            setStockInfo({});
                            formData.items.forEach(item => {
                              if (item.variant_id) fetchStockInfo(item.variant_id, wid);
                            });
                          }}
                          displayEmpty
                          sx={{ bgcolor: 'background.paper' }}
                        >
                          <MenuItem value=""><em>All Warehouses</em></MenuItem>
                          {warehouses.map((wh) => (
                            <MenuItem key={wh.id} value={wh.id}>
                              {wh.name}{wh.city ? ` — ${wh.city}` : ''}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {!selectedWarehouseId && (
                        <Typography variant="caption" color="warning.dark">
                          ⚠ Select a warehouse to see accurate per-warehouse stock
                        </Typography>
                      )}
                      {selectedWarehouseId && warehouses.find(w => w.id === selectedWarehouseId) && (
                        <Typography variant="caption" color="text.secondary">
                          Stock shown is for {warehouses.find(w => w.id === selectedWarehouseId)?.name}
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  {/* First Row: Customer selection with Add button and Dates */}
                  <Grid item xs={12} md={6}>
                    <Box display="flex" gap={1} alignItems="flex-start">
                      <Autocomplete
                        fullWidth
                        options={customersData?.items || []}
                        getOptionLabel={(option: any) => {
                          const name = option.name || option.business_name || option.billing_name || 'Unnamed';
                          const mobile = option.mobile_no || option.mobile || 'No Mobile';
                          return `${name} (${mobile})`;
                        }}
                        getOptionKey={(option: any) => option.id}
                        value={selectedCustomer}
                        onChange={(_, newValue) => {
                          setSelectedCustomer(newValue);
                          setFormData(prev => ({
                            ...prev,
                            customer_id: newValue?.id || ''
                          }));
                        }}
                        disabled={isConvertingFromEstimate}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Customer"
                            size="small"
                            required
                            error={!!errors.customer_id}
                            helperText={errors.customer_id}
                            placeholder={customersData?.items?.length ? "Search Name or Mobile" : "Loading..."}
                          />
                        )}
                        renderOption={(props, option: any) => (
                          <Box component="li" {...props} key={option.id}>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              <Typography variant="body2" fontWeight={600}>
                                {option.name || option.business_name || 'Unnamed Customer'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {option.mobile_no || option.mobile || 'No Mobile'} | {option.email || 'No Email'}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      />
                      {!isConvertingFromEstimate && (
                        <Tooltip title="Add New Customer">
                          <IconButton 
                            color="primary" 
                            size="small"
                            onClick={() => setOpenCustomerDialog(true)}
                            sx={{ 
                              border: '1px solid', 
                              borderColor: 'primary.main',
                              flexShrink: 0,
                              mt: 0.5
                            }}
                          >
                            <AddIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Order Date"
                      type="date"
                      value={formData.order_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, order_date: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      required
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Expected Delivery"
                      type="date"
                      value={formData.expected_delivery_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, expected_delivery_date: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  {/* Second Row: Charges and Address Cleanup */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Shipping Charges"
                      type="number"
                      value={formData.shipping_charges}
                      onChange={(e) => setFormData(prev => ({ ...prev, shipping_charges: parseFloat(e.target.value) || 0 }))}
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Packing Charges"
                      type="number"
                      value={formData.packing_charges}
                      onChange={(e) => setFormData(prev => ({ ...prev, packing_charges: parseFloat(e.target.value) || 0 }))}
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Button
                      fullWidth
                      size="small"
                      variant="outlined"
                      color="secondary"
                      onClick={() => setFormData(prev => ({ 
                        ...prev, 
                        billing_address_id: '', 
                        shipping_address_id: '' 
                      }))}
                      sx={{ height: '40px' }}
                    >
                      Clear All Addresses
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Addresses - Collapsible/Optional */}
          {formData.customer_id && (
            <Grid item xs={12}>
              <Card>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Billing Address (Optional)</InputLabel>
                        <Select
                          value={formData.billing_address_id}
                          onChange={(e) => setFormData(prev => ({ ...prev, billing_address_id: e.target.value }))}
                          label="Billing Address (Optional)"
                          disabled={addressesLoading}
                        >
                          <MenuItem value="">None</MenuItem>
                          {customerAddresses.filter(addr => addr.address_type === 'billing' || addr.address_type === 'both').map((address) => (
                            <MenuItem key={address.id} value={address.id}>
                              {address.address_line1}, {address.city}, {address.state} - {address.postal_code}
                              {address.is_default && ' (Default)'}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Shipping Address (Optional)</InputLabel>
                        <Select
                          value={formData.shipping_address_id}
                          onChange={(e) => setFormData(prev => ({ ...prev, shipping_address_id: e.target.value }))}
                          label="Shipping Address (Optional)"
                          disabled={addressesLoading}
                        >
                          <MenuItem value="">None</MenuItem>
                          {customerAddresses.filter(addr => addr.address_type === 'shipping' || addr.address_type === 'both').map((address) => (
                            <MenuItem key={address.id} value={address.id}>
                              {address.address_line1}, {address.city}, {address.state} - {address.postal_code}
                              {address.is_default && ' (Default)'}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Line Items */}
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Line Items ({formData.items.length})
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addLineItem}
                    sx={{ minWidth: 'auto', px: 1.5 }}
                  >
                    Add
                  </Button>
                </Box>

                {errors.items && (
                  <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>
                    {errors.items}
                  </Alert>
                )}

                <TableContainer sx={{ maxHeight: '450px', overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '40px' }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', minWidth: '200px' }}>Product</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '80px' }}>MRP</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '80px' }}>
                          Stock{selectedWarehouseId && warehouses.find(w => w.id === selectedWarehouseId) ? ` (${warehouses.find(w => w.id === selectedWarehouseId)?.name.split(' ')[0]})` : ''}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '70px' }}>Qty</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '90px' }}>Unit Price</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '70px' }}>Disc %</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '90px' }}>Taxable</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '70px' }}>Tax %</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '90px' }}>Tax Amt</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '100px' }}>Amount</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '50px' }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.items.map((item, index) => {
                        const qty = Number(item.quantity) || 0;
                        const price = Number(item.unit_price) || 0;
                        const discountPercent = Number(item.discount_percent) || 0;
                        
                        const lineTotal = qty * price;
                        const discountAmount = lineTotal * (discountPercent / 100);
                        const taxableAmount = lineTotal - discountAmount;
                        const taxRate = 18; // Default GST rate
                        const taxAmount = taxableAmount * (taxRate / 100);
                        const finalAmount = taxableAmount + taxAmount;
                        
                        // Get selected variant to show MRP — include product-level B2B fields
                        const selectedVariant = productsData?.items?.flatMap((p: any) => 
                          (p.variants || []).map((v: any) => ({
                            ...v,
                            product_name: p.name,
                            product_id: p.id,
                            is_set_selling: p.is_set_selling || false,
                            set_size: p.set_size || 1,
                            moq: p.moq || 1,
                            carton_quantity: p.carton_quantity || null,
                            inner_box_quantity: p.inner_box_quantity || null,
                            product_unit: p.product_unit || 'pcs',
                          }))
                        ).find((v: any) => v.id === item.variant_id);

                        // Compute CTN info for display
                        const ctnQty = selectedVariant?.carton_quantity;
                        const setSize = selectedVariant?.set_size || 1;
                        const isSetSelling = selectedVariant?.is_set_selling || false;
                        const ctnSets = ctnQty ? Math.floor(ctnQty / setSize) : null;
                        const ctnBoxes = isSetSelling && ctnQty ? Math.floor(ctnQty / setSize) : null;

                        return (
                          <TableRow key={index} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                            <TableCell sx={{ py: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                              {index + 1}
                            </TableCell>
                            <TableCell sx={{ py: 0.5 }}>
                              <Autocomplete
                                options={productsData?.items?.flatMap((p: any) => 
                                  (p.variants || []).map((v: any) => ({
                                    ...v,
                                    product_name: p.name,
                                    product_id: p.id,
                                    hsn_code: p.hsn_code,
                                    is_set_selling: p.is_set_selling || false,
                                    set_size: p.set_size || 1,
                                    moq: p.moq || 1,
                                    carton_quantity: p.carton_quantity || null,
                                    inner_box_quantity: p.inner_box_quantity || null,
                                    product_unit: p.product_unit || 'pcs',
                                  }))
                                ) || []}
                                getOptionLabel={(option: any) => {
                                  const stockAvailable = option.total_available ?? stockInfo[option.id]?.available_stock;
                                  const stockText = stockAvailable !== undefined ? ` (${stockAvailable} available)` : '';
                                  return `${option.product_name || 'Unknown'} - ${option.name}${stockText}`;
                                }}
                                getOptionKey={(option: any) => option.id}
                                value={selectedVariant || null}
                                onChange={(_, newValue) => {
                                  if (newValue) {
                                    const basePrice = Number(newValue.sale_price || newValue.mrp || 0);
                                    const sz = newValue.set_size || 1;
                                    // For set-selling products, unit_price = price per set (base_price * set_size)
                                    // For unit products, unit_price = base_price per unit
                                    const unitPrice = newValue.is_set_selling ? basePrice * sz : basePrice;
                                    // Initial quantity = MOQ (in sets for set-selling, in units for unit products)
                                    const initialQty = newValue.moq || 1;
                                    
                                    // Fetch stock info for this variant (for selected warehouse)
                                    fetchStockInfo(newValue.id, selectedWarehouseId);
                                    
                                    setFormData(prev => ({
                                      ...prev,
                                      items: prev.items.map((item, i) => 
                                        i === index ? {
                                          ...item,
                                          variant_id: newValue.id,
                                          unit_price: unitPrice,
                                          quantity: initialQty,
                                          hsn_code: newValue.hsn_code || '',
                                          discount_percent: Number(item.discount_percent) || 0
                                        } : item
                                      )
                                    }));
                                  } else {
                                    updateLineItem(index, 'variant_id', '');
                                    updateLineItem(index, 'unit_price', 0);
                                  }
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    size="small"
                                    placeholder="Select product"
                                    error={!!errors[`item_${index}_variant`]}
                                    sx={{ 
                                      minWidth: 200,
                                      '& .MuiInputBase-root': { fontSize: '0.8125rem', py: 0.25 },
                                      '& .MuiInputBase-input': { py: '4px !important' }
                                    }}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                              {selectedVariant ? (
                                <Box>
                                  {isSetSelling ? (
                                    <Tooltip title={`Set of ${setSize} ${selectedVariant.product_unit || 'pcs'} | MRP/unit: ₹${Number(selectedVariant.mrp || 0).toFixed(2)}`}>
                                      <Box>
                                        <Typography variant="caption" display="block" color="success.main" fontWeight={600}>
                                          SET×{setSize}
                                        </Typography>
                                        <Typography variant="caption" display="block">
                                          MRP/set: ₹{(Number(selectedVariant.mrp || 0) * setSize).toFixed(2)}
                                        </Typography>
                                      </Box>
                                    </Tooltip>
                                  ) : (
                                    <Box>
                                      <Typography variant="caption" display="block">
                                        ₹{Number(selectedVariant.mrp || 0).toFixed(2)}
                                      </Typography>
                                      <Typography variant="caption" display="block" color="text.disabled">
                                        MOQ: {selectedVariant.moq || 1} {selectedVariant.product_unit || 'pcs'}
                                      </Typography>
                                    </Box>
                                  )}
                                  {ctnQty && (
                                    <Typography variant="caption" display="block" color="info.main">
                                      CTN: {isSetSelling ? `${ctnBoxes} sets` : `${ctnQty} pcs`}
                                    </Typography>
                                  )}
                                </Box>
                              ) : '-'}
                            </TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                              {selectedVariant ? (
                                <Tooltip title={`Total: ${selectedVariant.total_stock ?? stockInfo[item.variant_id]?.current_stock ?? 0} | Reserved: ${selectedVariant.total_reserved ?? stockInfo[item.variant_id]?.reserved_stock ?? 0}`}>
                                  <span>{selectedVariant.total_available ?? stockInfo[item.variant_id]?.available_stock ?? 0}</span>
                                </Tooltip>
                              ) : '-'}
                            </TableCell>
                            <TableCell sx={{ py: 0.5 }}>
                              <Box>
                                <TextField
                                  size="small"
                                  value={item.quantity}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === '' || /^\d+$/.test(val)) {
                                      updateLineItem(index, 'quantity', val === '' ? '' : parseInt(val));
                                    }
                                  }}
                                  sx={{ 
                                    width: 65,
                                    '& .MuiInputBase-input': { fontSize: '0.8125rem', py: 0.5, px: 0.75 }
                                  }}
                                  error={!!errors[`item_${index}_quantity`] || (item.variant_id && (Number(item.quantity) || 0) > (selectedVariant?.total_available ?? stockInfo[item.variant_id]?.available_stock ?? 0))}
                                />
                                {selectedVariant && (
                                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>
                                    {isSetSelling ? `sets (×${setSize})` : (selectedVariant.product_unit || 'pcs')}
                                  </Typography>
                                )}
                                {item.variant_id && (
                                  <Box sx={{ mt: 0.5 }}>
                                    {(Number(item.quantity) || 0) > (selectedVariant?.total_available ?? stockInfo[item.variant_id]?.available_stock ?? 0) ? (
                                      <Chip 
                                        label={`⚠ Only ${selectedVariant?.total_available ?? stockInfo[item.variant_id]?.available_stock ?? 0} available`}
                                        size="small"
                                        color="error"
                                        sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                                      />
                                    ) : (selectedVariant?.total_available ?? stockInfo[item.variant_id]?.available_stock ?? 0) <= 5 ? (
                                      <Chip 
                                        label={`${selectedVariant?.total_available ?? stockInfo[item.variant_id]?.available_stock ?? 0} in stock`}
                                        size="small"
                                        color="warning"
                                        sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                                      />
                                    ) : (
                                      <Chip 
                                        label={`${selectedVariant?.total_available ?? stockInfo[item.variant_id]?.available_stock ?? 0} available`}
                                        size="small"
                                        color="success"
                                        sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                                      />
                                    )}
                                  </Box>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ py: 0.5 }}>
                              <TextField
                                size="small"
                                value={item.unit_price}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (val === '' || /^\d*\.?\d*$/.test(val)) {
                                    updateLineItem(index, 'unit_price', val);
                                  }
                                }}
                                sx={{ 
                                  width: 85,
                                  '& .MuiInputBase-input': { fontSize: '0.8125rem', py: 0.5, px: 0.75 }
                                }}
                                error={!!errors[`item_${index}_price`]}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.5 }}>
                              <TextField
                                size="small"
                                value={item.discount_percent}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (val === '' || /^\d*\.?\d*$/.test(val)) {
                                    updateLineItem(index, 'discount_percent', val);
                                  }
                                }}
                                sx={{ 
                                  width: 65,
                                  '& .MuiInputBase-input': { fontSize: '0.8125rem', py: 0.5, px: 0.75 }
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.8125rem', fontWeight: 500 }}>
                              ₹{taxableAmount.toFixed(2)}
                            </TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                              {taxRate}%
                            </TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.8125rem', color: 'info.main' }}>
                              ₹{taxAmount.toFixed(2)}
                            </TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.8125rem', fontWeight: 600, color: 'primary.main' }}>
                              ₹{finalAmount.toFixed(2)}
                            </TableCell>
                            <TableCell sx={{ py: 0.5 }}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => removeLineItem(index)}
                                disabled={formData.items.length === 1}
                                sx={{ p: 0.5 }}
                              >
                                <DeleteIcon sx={{ fontSize: '1rem' }} />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Calculation Summary */}
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                {/* Calculation Preview - Compact */}
                <Box>
                  <Divider sx={{ mb: 1.5 }} />
                  <Grid container spacing={1.5} justifyContent="flex-end">
                    <Grid item xs={12} md={5}>
                      <Card variant="outlined">
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box display="flex" justifyContent="space-between" mb={0.5}>
                            <Typography variant="body2">Items Subtotal:</Typography>
                            <Typography variant="body2">{formatCurrency(itemsSubtotal)}</Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" mb={0.5}>
                            <Typography variant="body2">Shipping:</Typography>
                            <Typography variant="body2">{formatCurrency(formData.shipping_charges || 0)}</Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" mb={0.5}>
                            <Typography variant="body2">Packing:</Typography>
                            <Typography variant="body2">{formatCurrency(formData.packing_charges || 0)}</Typography>
                          </Box>
                          {otherChargesTotal > 0 && (
                            <Box display="flex" justifyContent="space-between" mb={0.5}>
                              <Typography variant="body2">Other Charges:</Typography>
                              <Typography variant="body2">{formatCurrency(otherChargesTotal)}</Typography>
                            </Box>
                          )}
                          <Box display="flex" justifyContent="space-between" mb={0.5}>
                            <Typography variant="body2">Taxable Amount:</Typography>
                            <Typography variant="body2">{formatCurrency(subtotal)}</Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" mb={0.5}>
                            <Typography variant="body2">GST (18%):</Typography>
                            <Typography variant="body2">{formatCurrency(taxAmount)}</Typography>
                          </Box>
                          <Divider sx={{ my: 0.75 }} />
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="subtitle1" fontWeight={600} color="primary">
                              Total Amount:
                            </Typography>
                            <Typography variant="subtitle1" fontWeight={600} color="primary">
                              {formatCurrency(total)}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            Items: {formData.items.length} | Total Qty: {formData.items.reduce((sum, item) => sum + item.quantity, 0)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Other Charges - Compact */}
          {(formData.other_charges || []).length > 0 && (
            <Grid item xs={12}>
              <Card>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Other Charges ({(formData.other_charges || []).length})
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={addOtherCharge}
                      sx={{ minWidth: 'auto', px: 1.5 }}
                    >
                      Add
                    </Button>
                  </Box>
                  {(formData.other_charges || []).map((charge, index) => (
                    <Grid container spacing={1} key={index} sx={{ mb: 1 }}>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Name"
                          value={charge.name}
                          onChange={(e) => updateOtherCharge(index, 'name', e.target.value)}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField
                          fullWidth
                          label="Amount"
                          type="number"
                          value={charge.amount}
                          onChange={(e) => updateOtherCharge(index, 'amount', parseFloat(e.target.value) || 0)}
                          inputProps={{ min: 0, step: 0.01 }}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Description"
                          value={charge.description || ''}
                          onChange={(e) => updateOtherCharge(index, 'description', e.target.value)}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={1}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeOtherCharge(index)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          )}
          {(formData.other_charges || []).length === 0 && (
            <Grid item xs={12}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={addOtherCharge}
                sx={{ ml: 1 }}
              >
                Add Other Charges
              </Button>
            </Grid>
          )}

          {/* Notes - Compact Inline */}
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <TextField
                  fullWidth
                  label="Order Notes"
                  multiline
                  rows={2}
                  size="small"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.8125rem' } }}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </form>

      {/* Quick Add Customer Dialog */}
      <CustomerQuickAddDialog
        open={openCustomerDialog}
        onClose={() => setOpenCustomerDialog(false)}
        customerGroups={customerGroups}
        onCreated={handleQuickCustomerCreated}
      />
    </Box>
  );
};

export default SalesOrderForm;